/**
 * Componente Global de Confirmación - Tipo 1
 *
 * Componente reutilizable para todas las ventanas de confirmación del sistema.
 * Basado en el diseño final aprobado.
 *
 * Soporta dos modos:
 * - Modo simple: diseño estándar con icono centrado
 * - Modo con header: header con color de fondo (para flujos complejos)
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Typography,
  Box,
  IconButton,
  Button,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { Close as CloseIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { colors } from '../../../styles/theme/colors';
import { useTabScopedOpen } from '../../dashboard/TabPanelActiveContext';

export type ConfirmationStatus =
  | 'initial'
  | 'confirming'
  | 'confirmed'
  | 'canceling'
  | 'canceled'
  | null;

export interface ConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  icon?: React.ReactNode;
  variant?: 'default' | 'danger' | 'warning' | 'info';
  loading?: boolean;
  disabled?: boolean;
  // Opciones avanzadas
  requireCheckbox?: boolean;
  checkboxLabel?: string;
  customContent?: React.ReactNode;

  // Nuevas opciones para header con color
  headerVariant?: 'default' | 'colored';
  headerColor?: 'emerald' | 'red' | 'blue';
  showCloseButton?: boolean; // Para ocultar el botón X cuando hay header coloreado

  /** Sobrescribe el degradado del botón confirmar (p. ej. catálogo de productos) */
  confirmButtonGradient?: string;
  /** Hover del botón confirmar cuando se usa confirmButtonGradient */
  confirmButtonGradientHover?: string;
  /** Color del icono grande (modo header default); si no se pasa, se usa el verde de acento habitual */
  accentIconColor?: string;

  // Soporte para múltiples estados (flujos complejos)
  status?: ConfirmationStatus;
  onCompleteConfirmation?: () => void;
  onCompleteCancellation?: () => void;
  onReset?: () => void;
  // Mensajes por estado
  confirmingMessage?: string;
  confirmedMessage?: string;
  confirmedSubtitle?: string;
  cancelingMessage?: string;
  canceledMessage?: string;
  canceledSubtitle?: string;
}

/**
 * Modal de confirmación unificado para todo el sistema
 *
 * Diseño final aprobado:
 * - Tamaño: 400px
 * - Icono: 48px sin fondo (modo default) o en header (modo colored)
 * - Tipografía: Título 1.3125rem, Mensaje 0.9375rem
 * - Padding: 36px/30px
 * - Botones: 120px, padding 9px/18px
 * - Cierre: Solo con X o ESC (configurable)
 */
const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  icon,
  variant = 'default',
  loading = false,
  disabled = false,
  requireCheckbox = false,
  checkboxLabel,
  customContent,
  confirmButtonGradient,
  confirmButtonGradientHover,
  accentIconColor,
  headerVariant = 'default',
  headerColor = 'emerald',
  showCloseButton = true,
  status = null,
  onCompleteConfirmation,
  onCompleteCancellation,
  onReset,
  confirmingMessage,
  confirmedMessage,
  confirmedSubtitle = 'Redirigiendo...',
  cancelingMessage,
  canceledMessage,
  canceledSubtitle = 'Cerrando...',
}) => {
  const scopedOpen = useTabScopedOpen(open);
  const [isChecked, setIsChecked] = useState(false);

  // Resetear checkbox cuando el modal se abre/cierra
  useEffect(() => {
    if (!open) {
      setIsChecked(false);
    }
  }, [open]);

  // Determinar el color del header según el estado y variante
  const getHeaderColor = (): 'emerald' | 'red' => {
    if (headerVariant === 'colored') {
      if (status === 'canceling' || status === 'canceled') {
        return 'red';
      }
      return headerColor === 'red' ? 'red' : 'emerald';
    }
    return 'emerald'; // No se usa si headerVariant es 'default'
  };

  // Determinar el título del modal según el estado
  const getModalTitle = (): string => {
    if (status === 'canceling' || status === 'canceled') {
      return 'Cancelar registro';
    }
    if (status === 'confirming' || status === 'confirmed') {
      return 'Confirmar registro';
    }
    return title;
  };

  // Determinar el contenido según el estado
  const getContent = () => {
    if (!status) {
      /** Un solo texto de cuerpo: message explícito, o title solo si no hay customContent */
      const bodyText = message ?? (customContent ? undefined : title);
      return {
        type: 'initial' as const,
        title: bodyText ?? title,
        bodyText,
        showButtons: true,
        primaryAction: confirmText,
        secondaryAction: cancelText,
      };
    }

    if (status === 'confirming') {
      return {
        type: 'confirming' as const,
        title: confirmingMessage || message || title,
        showButtons: true,
        primaryAction: 'Sí, confirmar',
        secondaryAction: 'Regresar',
      };
    }

    if (status === 'confirmed') {
      return {
        type: 'confirmed' as const,
        title: confirmedMessage || message || title,
        subtitle: confirmedSubtitle,
        showButtons: false,
      };
    }

    if (status === 'canceling') {
      return {
        type: 'canceling' as const,
        title: cancelingMessage || message || title,
        showButtons: true,
        primaryAction: 'Sí, cancelar',
        secondaryAction: 'Regresar',
      };
    }

    if (status === 'canceled') {
      return {
        type: 'canceled' as const,
        title: canceledMessage || message || title,
        subtitle: canceledSubtitle,
        showButtons: false,
      };
    }

    return {
      type: 'initial' as const,
      title: message || title,
      showButtons: true,
      primaryAction: confirmText,
      secondaryAction: cancelText,
    };
  };

  // Determinar las acciones de los botones
  const getButtonActions = () => {
    if (!status) {
      return {
        primary: onConfirm,
        secondary: onClose,
      };
    }

    if (status === 'confirming') {
      return {
        primary: onCompleteConfirmation || onConfirm,
        secondary: onReset || onClose,
      };
    }

    if (status === 'canceling') {
      return {
        primary: onCompleteCancellation || onClose,
        secondary: onReset || onClose,
      };
    }

    return {
      primary: onConfirm,
      secondary: onClose,
    };
  };

  const content = getContent();
  const buttonActions = getButtonActions();
  const currentHeaderColor = getHeaderColor();
  const modalTitle = getModalTitle();

  // Colores según variante
  const getVariantColors = () => {
    const defaultGradient = `linear-gradient(to right, ${colors.primary.main}, ${colors.success.main})`;
    const emeraldGradient = 'linear-gradient(to right, #06b6d4, #10b981)';
    const redGradient = `linear-gradient(to right, ${colors.error.main}, ${colors.error.dark})`;

    switch (variant) {
      case 'danger':
        return {
          iconColor: colors.error.main,
          confirmGradient: redGradient,
          headerGradient: redGradient,
        };
      case 'warning':
        return {
          iconColor: colors.warning.main,
          confirmGradient: `linear-gradient(to right, ${colors.warning.main}, ${colors.warning.dark})`,
          headerGradient: `linear-gradient(to right, ${colors.warning.main}, ${colors.warning.dark})`,
        };
      case 'info':
        return {
          iconColor: colors.info.main,
          confirmGradient: defaultGradient,
          headerGradient: emeraldGradient,
        };
      default:
        return {
          iconColor: colors.info.main,
          confirmGradient: defaultGradient,
          headerGradient: emeraldGradient,
        };
    }
  };

  const variantColors = getVariantColors();
  const effectiveConfirmGradient = confirmButtonGradient ?? variantColors.confirmGradient;
  const effectiveConfirmGradientHover =
    confirmButtonGradientHover ?? confirmButtonGradient ?? variantColors.confirmGradient;

  const checkboxAccentColor = confirmButtonGradient ? colors.primary.main : colors.success.main;

  const headerGradient =
    currentHeaderColor === 'red'
      ? `linear-gradient(to right, ${colors.error.main}, ${colors.error.dark})`
      : 'linear-gradient(to right, #06b6d4, #10b981)';

  // Renderizar icono de estado
  const renderStatusIcon = () => {
    if (content.type === 'confirmed') {
      return (
        <Box
          sx={{
            width: '4rem',
            height: '4rem',
            backgroundColor: colors.success.light,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px auto',
            '& svg': {
              fontSize: '2rem',
              color: '#059669',
            },
          }}
        >
          <CheckCircleIcon />
        </Box>
      );
    }

    if (content.type === 'canceled') {
      return (
        <Box
          sx={{
            width: '4rem',
            height: '4rem',
            backgroundColor: colors.grey[100],
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px auto',
            '& svg': {
              fontSize: '2rem',
              color: colors.grey[500],
            },
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="15" y1="9" x2="9" y2="15"></line>
            <line x1="9" y1="9" x2="15" y2="15"></line>
          </svg>
        </Box>
      );
    }

    return null;
  };

  return (
    <Dialog
      open={scopedOpen}
      onClose={(event, reason) => {
        // Solo permitir cerrar con ESC, ignorar completamente el backdropClick
        // Mientras loading, no permitir cerrar (evita abandonar un submit en curso)
        if (loading) {
          return;
        }
        if (reason === 'escapeKeyDown') {
          if (onReset && status) {
            onReset();
          } else {
            onClose();
          }
        }
      }}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: (theme) => ({
          borderRadius: '16px',
          boxShadow:
            theme.palette.mode === 'dark'
              ? '0 24px 48px rgba(0, 0, 0, 0.55)'
              : '0 20px 60px rgba(0, 0, 0, 0.15)',
          overflow: 'hidden',
          maxWidth: '400px',
          m: 2,
          backgroundColor: theme.palette.background.paper,
          backgroundImage: 'none',
          ...(theme.palette.mode === 'dark'
            ? { border: `1px solid ${theme.palette.divider}` }
            : {}),
        }),
      }}
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
        },
      }}
    >
      {/* Header con color (si headerVariant es 'colored') */}
      {headerVariant === 'colored' && (
        <Box
          sx={{
            background: headerGradient,
            padding: '16px 24px',
            display: 'flex',
            alignItems: 'center',
            color: colors.background.paper,
          }}
        >
          {icon && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                marginRight: '8px',
                '& svg': {
                  fontSize: '22px',
                  color: colors.background.paper,
                },
              }}
            >
              {icon}
            </Box>
          )}
          <Typography
            variant="h6"
            sx={{
              fontSize: '1.125rem',
              fontWeight: 500,
              color: colors.background.paper,
              flex: 1,
            }}
          >
            {modalTitle}
          </Typography>
        </Box>
      )}

      {/* Botón de cerrar flotante (solo si showCloseButton es true y no hay header coloreado) */}
      {showCloseButton && headerVariant !== 'colored' && (
        <IconButton
          onClick={onClose}
          disabled={loading || disabled}
          sx={(theme) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            zIndex: 1,
            backgroundColor:
              theme.palette.mode === 'dark' ? theme.palette.action.hover : colors.grey[100],
            color: theme.palette.text.secondary,
            width: 32,
            height: 32,
            '&:hover': {
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? alpha(theme.palette.common.white, 0.12)
                  : colors.grey[200],
            },
          })}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      )}

      {/* Contenido centrado y espacioso */}
      <DialogContent
        sx={{
          padding: headerVariant === 'colored' ? '24px' : '36px 30px 30px 30px',
          textAlign: 'center',
          pt: headerVariant === 'colored' ? '24px' : '36px',
        }}
      >
        {/* Icono sin fondo (solo en modo default) */}
        {headerVariant === 'default' && icon && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px auto',
              '& svg': {
                fontSize: 48,
                color: accentIconColor ?? colors.success.main,
              },
            }}
          >
            {icon}
          </Box>
        )}

        {/* Estados de confirmación/cancelación completados */}
        {renderStatusIcon()}

        {/* Texto de confirmación (un solo mensaje; no duplicar title si hay customContent) */}
        {headerVariant === 'default' &&
          (content.type === 'initial'
            ? 'bodyText' in content && content.bodyText
            : content.title) && (
            <Typography
              variant="h6"
              sx={(theme) => ({
                fontWeight: 600,
                color: theme.palette.text.primary,
                fontSize: '1.3125rem',
                marginBottom: content.type === 'initial' && customContent ? '16px' : '28px',
                lineHeight: 1.3,
              })}
            >
              {content.type === 'initial' && 'bodyText' in content && content.bodyText
                ? content.bodyText
                : content.title}
            </Typography>
          )}

        {/* Mensaje (solo en estados iniciales con header coloreado) */}
        {headerVariant === 'colored' &&
          (content.type === 'initial' ||
            content.type === 'confirming' ||
            content.type === 'canceling') && (
            <Typography
              variant="body1"
              sx={(theme) => ({
                color: theme.palette.text.primary,
                fontSize: '1rem',
                marginBottom: '24px',
              })}
            >
              {content.title}
            </Typography>
          )}

        {/* Subtítulo para estados completados */}
        {content.subtitle && (
          <Typography
            variant="body2"
            sx={(theme) => ({
              color: theme.palette.text.secondary,
              fontSize: '0.875rem',
              marginBottom: '28px',
            })}
          >
            {content.subtitle}
          </Typography>
        )}

        {/* Contenido personalizado */}
        {customContent && (
          <Box sx={{ marginBottom: requireCheckbox ? '20px' : '28px' }}>{customContent}</Box>
        )}

        {/* Checkbox de confirmación (opcional) */}
        {requireCheckbox &&
          (content.type === 'initial' ||
            content.type === 'confirming' ||
            content.type === 'canceling') && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={isChecked}
                  onChange={(e) => setIsChecked(e.target.checked)}
                  disabled={loading || disabled}
                  sx={{
                    color: checkboxAccentColor,
                    '&.Mui-checked': {
                      color: checkboxAccentColor,
                    },
                  }}
                />
              }
              label={
                <Typography
                  variant="body2"
                  sx={(theme) => ({
                    fontSize: '0.875rem',
                    color: theme.palette.text.primary,
                  })}
                >
                  {checkboxLabel || 'Confirmo esta acción'}
                </Typography>
              }
              sx={{
                marginBottom: '28px',
                marginLeft: 0,
                alignItems: 'flex-start',
                '& .MuiFormControlLabel-label': { color: 'inherit' },
              }}
            />
          )}

        {/* Botones (solo si showButtons es true) */}
        {content.showButtons && (
          <Box
            sx={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'center',
              flexDirection: { xs: 'column', sm: 'row' },
            }}
          >
            <Button
              onClick={buttonActions.secondary}
              disabled={loading || disabled}
              sx={(theme) => ({
                minWidth: { xs: '100%', sm: '120px' },
                padding: '9px 18px',
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.875rem',
                color:
                  theme.palette.mode === 'dark'
                    ? theme.palette.text.primary
                    : colors.text.secondary,
                // Mismo fondo que el Paper del modal (ventana)
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                  color: theme.palette.text.primary,
                },
              })}
            >
              {content.secondaryAction}
            </Button>
            <Button
              onClick={buttonActions.primary}
              variant="contained"
              autoFocus
              disabled={loading || disabled || (requireCheckbox && !isChecked)}
              sx={{
                minWidth: { xs: '100%', sm: '120px' },
                padding: '9px 18px',
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.875rem',
                background:
                  content.type === 'canceling'
                    ? `linear-gradient(to right, ${colors.error.main}, ${colors.error.dark})`
                    : headerVariant === 'colored'
                      ? headerGradient
                      : effectiveConfirmGradient,
                color: colors.background.paper,
                boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)',
                '&:hover': {
                  background:
                    content.type === 'canceling'
                      ? `linear-gradient(to right, ${colors.error.dark}, ${colors.error.main})`
                      : headerVariant === 'colored'
                        ? headerGradient
                        : effectiveConfirmGradientHover,
                  boxShadow: '0 6px 16px rgba(37, 99, 235, 0.4)',
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.2s ease-in-out',
                '&:disabled': {
                  opacity: 0.5,
                  cursor: 'not-allowed',
                },
              }}
            >
              {loading ? 'Procesando...' : content.primaryAction}
            </Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmationModal;
